import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { ExceptionInterceptor } from './core/interceptors/exception.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ObserverAuthInterceptor } from './observer/interceptors/observer-auth/observer-auth.interceptor';
import { ToastService, TWO_FACTOR_CLIENT } from '@ui-lib/components';
import {
  IdentityApiConfiguration,
  IdentityApiModule,
  TwoFactorClient,
} from './core/openapi/identity';
import { AuthConfigService } from './core/services/auth/auth-config.service';
import { UserInformationService } from './core/services/user/user-information.service';
import { APP_ROUTES_NATIVE } from './app.routes.native';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  NotificationApiConfiguration,
  NotificationApiModule,
} from './core/openapi/notification';
import {
  BffUserPortalApiConfiguration,
  BffUserPortalApiModule,
} from './core/openapi/bff-user-portal';
import { environment } from '../environments/environment';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { ObservableAccountService } from './observer/services/observable-account.service';
import { LocalStorageService } from './core/services/storage/local-storage.service';
import { LanguageService } from './core/services/language/language.service';
import './core/extensions/toast.service.extension';
import { Capacitor } from '@capacitor/core';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'chart.js';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { APP_ROUTES_WEB } from './app.routes.web';

Chart.register(zoomPlugin);

// Factory function for TranslateHttpLoader
export function HttpLoaderFactory(httpClient: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient, 'assets/i18n/', '.json');
}

const routes = Capacitor.isNativePlatform()
  ? APP_ROUTES_NATIVE
  : APP_ROUTES_WEB;

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideOAuthClient(),
    provideCharts(withDefaultRegisterables()),
    ToastService,
    UserInformationService,
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'ru',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      IdentityApiModule.forRoot(() => {
        return new IdentityApiConfiguration({
          basePath: environment.apiUrls.identity,
        });
      }),
      NotificationApiModule.forRoot(() => {
        return new NotificationApiConfiguration({
          basePath: environment.apiUrls.notification,
        });
      }),
      BffUserPortalApiModule.forRoot(() => {
        return new BffUserPortalApiConfiguration({
          basePath: environment.apiUrls.bffUserPortal,
        });
      }),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ExceptionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ObserverAuthInterceptor,
      multi: true,
    },
    {
      provide: TWO_FACTOR_CLIENT,
      useClass: TwoFactorClient,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (authConfigService: AuthConfigService) => {
        return () =>
          Capacitor.isNativePlatform()
            ? authConfigService.initWithMobile()
            : authConfigService.init();
      },
      multi: true,
      deps: [AuthConfigService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageService: LanguageService) => {
        return () => languageService.initCurrentLanguage();
      },
      multi: true,
      deps: [LanguageService],
    },
    LocalStorageService,
    AuthConfigService,
    LanguageService,
    {
      provide: LOCALE_ID,
      useValue: 'ru-RU',
    },
    ObservableAccountService,
  ],
};
